// Shared camera tuning for the EAN scanners (FoodSearchDialog, PantryBarcodeDialog, EanScanner).
// Android specifics learned on device: (1) `facingMode: environment` may pick an
// ultra-wide / macro lens without autofocus on multi-lens phones, (2) setting
// focusDistance or "single-shot" switches continuous AF OFF and leaves the
// image blurry. So on Android we pick the main back lens explicitly and only
// ever ask for continuous AF.
export const IS_ANDROID = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

const BACK_RE = /back|rear|environment|facing back|rück|hinten|posteriore|arrière/i;

/** Main back camera deviceId on Android (lowest camera index facing back), else null. */
export async function pickBackCameraId(Html5Qrcode) {
  if (!IS_ANDROID) return null;
  try {
    const cams = (await Html5Qrcode.getCameras()) || [];
    const back = cams.filter((c) => BACK_RE.test(c.label || ""));
    const pool = back.length ? back : cams;
    if (!pool.length) return null;
    const idx = (c) => { const m = /(\d+)/.exec(c.label || ""); return m ? parseInt(m[1], 10) : 999; };
    pool.sort((a, b) => idx(a) - idx(b));
    return pool[0].id;
  } catch { return null; }
}

export function barcodeVideoConstraints(deviceId) {
  const base = { width: { ideal: 1920 }, height: { ideal: 1080 }, advanced: [{ focusMode: "continuous" }] };
  return deviceId ? { deviceId: { exact: deviceId }, ...base } : { facingMode: { ideal: "environment" }, ...base };
}

/** Continuous AF; the macro focusDistance trick stays non-Android only. */
export async function tuneFocus(track) {
  const caps = track?.getCapabilities?.() || {};
  const modes = caps.focusMode || [];
  try {
    if (!IS_ANDROID && caps.focusDistance && typeof caps.focusDistance.min === "number") {
      await track.applyConstraints({ advanced: [{ focusMode: "continuous", focusDistance: caps.focusDistance.min }] });
    } else if (!modes.length || modes.includes("continuous")) {
      await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
    }
  } catch { /* unsupported combo */ }
}

/** Haptic "success" on decode: native Haptics in the app, Vibration API on the web. */
export async function scanSuccessFeedback() {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (Capacitor.isNativePlatform()) {
      const { Haptics, NotificationType } = await import("@capacitor/haptics");
      await Haptics.notification({ type: NotificationType.Success });
      return;
    }
  } catch { /* plugin missing */ }
  try { navigator.vibrate?.(80); } catch { /* unsupported */ }
}

/** Scan window ≈ the whole camera frame: the code is found wherever it is, no aiming needed. */
export function bigScanBox(vw, vh) {
  return { width: Math.max(50, Math.floor(vw * 0.96)), height: Math.max(50, Math.floor(vh * 0.82)) };
}

/** Zoom range from the live track, or null when the camera doesn't expose one. */
export function readZoom(track, preferred) {
  const z = track?.getCapabilities?.()?.zoom;
  if (!z || typeof z.max !== "number") return null;
  const min = typeof z.min === "number" ? z.min : 1;
  if (!(z.max > min)) return null;
  const cur = track.getSettings?.()?.zoom;
  return { min, max: z.max, step: z.step || 0.1, value: preferred ?? cur ?? min };
}

export async function applyZoom(track, value) {
  if (!track) return false;
  try { await track.applyConstraints({ advanced: [{ zoom: value }] }); return true; } catch { return false; }
}

/** Periodic single-shot AF helps some iPhones/desktops; on Android it kills continuous AF. */
export const shouldPeriodicRefocus = () => !IS_ANDROID;

/** Tap-to-focus: one single-shot AF (with point of interest), then back to continuous. */
export async function refocusOnce(track, point) {
  if (!track) return;
  try {
    const adv = point ? [{ focusMode: "single-shot", pointsOfInterest: [point] }] : [{ focusMode: "single-shot" }];
    await track.applyConstraints({ advanced: adv });
  } catch {
    try { await track.applyConstraints({ advanced: [{ focusMode: "single-shot" }] }); } catch { /* noop */ }
  }
  setTimeout(() => { track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {}); }, 1500);
}
